import { useMemo, useRef } from 'react'
import { createUseStyles } from 'react-jss'
import { ThemeContextValue, useTheme } from './ThemeProvider'

/**
 * 主题感知的 JSS 样式工厂。
 *
 * 用法：
 *   const useStyles = useThemedStyles((t) => ({ bar: { background: t.palette.paper2 } }))
 *   const classes = useStyles()
 *
 * ⚠️ 工厂函数不要在组件外定义成闭包捕获 palette 的版本，直接内联传参即可。
 *
 * ─────────────────────────────────────────────────────────────────────────
 * ⚠️ 为什么是「样式函数 + 每次传 data.theme」而不是 `createUseStyles(factory(theme))`
 * ─────────────────────────────────────────────────────────────────────────
 * 以前的写法是 `useMemo(() => createUseStyles(factory(theme)), [theme])` ——
 * **看着完全合理，实际从来不生效**：明暗切换时组件确实重渲染、`theme` 确实是新对象、
 * `factory(theme)` 确实产出了新样式，但**页面一个颜色都不变**。
 *
 * 原因在 react-jss@10 内部（`createUseStyles.js`）：
 *
 *     const useTheme = (theme) =>
 *       typeof styles === 'function' ? (theme || useContext(ThemeContext) || noTheme) : noTheme
 *
 *     const [sheet] = useMemo(() => createStyleSheet({...}), [context, theme])
 *
 * `styles` 传**对象**时 `useTheme` 一律返回模块常量 `noTheme`，于是那个 useMemo 的依赖
 * `[context, noTheme]` **永远不变**；而 React 的 useMemo 缓存是按 hook 槽位存的，
 * 跟闭包身份无关 —— 换一个新的 `useStyles` 闭包进来，槽位里的旧 sheet 照样命中缓存被返回。
 * 结果就是：**首次挂载时的 sheet 被永久钉死，之后所有配色改动都被丢掉。**
 *
 * 更隐蔽的是它下面还有第二层缓存（`createStyleSheet`）：`manager.get(options.theme)`
 * 直接按 theme 对象复用已有 sheet —— 两层叠加，改 `theme` 传参也未必重建。
 *
 * 所以必须走 react-jss 的**主题样式**通道：`styles` 传**函数**，theme 由 `data.theme` 逐次传入。
 * 这时 `useTheme` 会返回 `data.theme`（即我们的主题对象），依赖真正开始变化，
 * 新 sheet 才会被建出来。`createUseStyles` 本身**只建一次**（依赖为空），
 * 所以也不会每次渲染都造新表。
 *
 * ⚠️ `factory` 用 ref 兜住：`createUseStyles` 只建一次，闭包里不能捕获首次的 factory。
 */
export function useThemedStyles<S extends Record<string, unknown>>(factory: (theme: ThemeContextValue) => S) {
	const theme = useTheme()

	// createUseStyles 只建一次，工厂从 ref 取最新的那份（HMR、或调用方换了工厂都对）
	const factoryRef = useRef(factory)
	factoryRef.current = factory

	// react-jss 的重载在「样式函数」这条路上推不出我们的泛型 S，这里显式收窄成它的主题样式签名
	const useStyles = useMemo(() => {
		const make = createUseStyles as unknown as (
			styles: (theme: ThemeContextValue) => Record<string, unknown>
		) => (data?: {theme?: ThemeContextValue}) => { [K in keyof S]: string }
		return make((t: ThemeContextValue) => factoryRef.current(t))
	}, [])

	// data 要 memo：它是 react-jss 内部 insertion effect 的依赖，每次渲染换新对象会白跑一遍
	const data = useMemo(() => ({theme}), [theme])

	return useStyles(data) as { [K in keyof S]: string }
}
